import { Login } from '@platform/client';
import { InlineFormInput } from '@platform/components';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { MovingWorldsBannerLogoLink } from '../../../layout/MovingWorldsLogo';
import corporateBackground from '../../background-images/CorporateSignUp.png';
import { BackToOptionsLink } from '../../components/BackToOptionsLink';
import { ContinueButton } from '../../components/ContinueButton';
import { LogInBackground } from '../../components/LogInBackground';
import { SetPasswordInput } from '../../components/SetPasswordInput';
import { ToSFormCheckbox } from '../../components/ToSCheckbox';
import { ROUTES } from '../../../routes/consts';

const CORPORATE_PARTNER_DOMAINS = [
  '@sap.com',
  '@f5.com',
  '@ey.com',
  '.ey.com',
  '@reckitt.com',
  '@lumen.com',
  '@philips.com',
  '@movingworlds.org', //Not a corporate partner, but can be used with +sap, +f5, etc. to test flows
];

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  termsOfService: boolean;
};

export const CorporateProSignupPage = () => {
  const [accountExists, setAccountExists] = useState(false);

  const signUpMutation = Login.useSignUpWithPassword({
    mutation: {
      onSuccess: () => {
        // Redirect to the email verification page
        window.location.href =
          '/platform/auth/email-verification?email=' + getValues('email') + '/';
      },
      onError: (error) => {
        if ((error as AxiosError).status === 401) {
          setAccountExists(true);
        }
      },
    },
  });

  const signUp: SubmitHandler<FormValues> = async (data) => {
    signUpMutation.mutate({
      data: {
        password: data.password,
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        isRep: false,
        impactPartnerId: null,
        orgInviteId: null,
        corporateClientId: null,
      },
    });
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<FormValues>({ mode: 'onBlur', reValidateMode: 'onSubmit' });

  return (
    <LogInBackground background={corporateBackground}>
      <div className='h-full'>
        <div className='md:hidden w-full m-auto text-brand-500 h-16 min-h-16 border-b border-gray-100'>
          <MovingWorldsBannerLogoLink
            className='flex justify-center h-full w-full items-center'
            height='22'
          />
        </div>
        <div className='flex flex-col w-full md:w-[400px] px-4 md:px-1 py-8 md:py-0 h-full md:items-center md:justify-center'>
          <div className='flex flex-col gap-8'>
            <h3 className='text-display-sm font-gray-700 font-medium'>Join your program</h3>
            <div>
              Use your work email address to help make sure we get you signed up for the correct
              cohort.
            </div>
            <form
              className='flex flex-col gap-8'
              name='corporate-pro-signup'
              onSubmit={handleSubmit(signUp)}
            >
              <div className='flex flex-row justify-between gap-4'>
                <InlineFormInput
                  label='First name'
                  placeholder='First name'
                  autocomplete='given-name'
                  register={() => {
                    return register('firstName', {
                      required: { value: true, message: 'Enter a first name.' },
                    });
                  }}
                  errors={errors.firstName}
                />
                <InlineFormInput
                  label='Last name'
                  placeholder='Last name'
                  autocomplete='family-name'
                  register={() => {
                    return register('lastName', {
                      required: { value: true, message: 'Enter a last name.' },
                    });
                  }}
                  errors={errors.lastName}
                />
              </div>
              <InlineFormInput
                label='Work email'
                placeholder='Enter your work email address'
                autocomplete='email'
                register={() => {
                  return register('email', {
                    required: { value: true, message: 'Please enter your email address.' },
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: 'Please enter a valid email address.',
                    },
                    validate: {
                      notCorporateEmail: (value) => {
                        //This surely won't cause a triage ticket down the road!
                        if (
                          CORPORATE_PARTNER_DOMAINS.some((domain) =>
                            value.toLocaleLowerCase().endsWith(domain),
                          )
                        ) {
                          return true;
                        }
                        return "Invalid email, make sure you're using a work email address.";
                      },
                    },
                  });
                }}
                errors={errors.email}
              />
              <SetPasswordInput
                password={getValues('password') || ''}
                register={register}
                errors={errors.password}
              />
              <ToSFormCheckbox register={register} errors={errors.termsOfService} />
              {accountExists && (
                <div className='border border-gray-200 bg-error-25 shadows-xs h-[52px] flex gap-1 items-center p-4 text-sm'>
                  This account already exists.
                  <a href='/platform/auth/login' className='underline text-brand-100'>
                    Log in to continue
                  </a>
                </div>
              )}
              <ContinueButton />
            </form>
            <BackToOptionsLink url={ROUTES.SIGN_UP_PRO} />
          </div>
        </div>
      </div>
    </LogInBackground>
  );
};
